import { Commit, ActionTree } from 'vuex';
import { State } from './database.store';
import { AxiosResponse } from 'axios';
import graphQl from '@/services/graphQl';
import { Context, Params } from '@/utils/types/global';
import graphHelpers from '@/utils/graphHelper';
import { KeyLabel } from '@/store/mutations-typesCommon';
import * as types from '@/components/Database/database.types';
import i18n from '@/plugins/i18n';
import * as constants from '@/utils/templates/databaseTemplate/database.constants';
import router from '@/router';

const actions: ActionTree<State, any> = {
  getDatabaseServices(
    context: Context,
    params: { duration: any; serviceReq: any, pagination: any, keyword: any }
  ) {
    if (!params.serviceReq) {
      params.serviceReq = {
        capability: 'D',
      };
    }
    return graphQl
      .query('queryServicesWithTotalCount')
      .params(params)
      .then((res: AxiosResponse) => {
        context.commit('setDatabaseServices', res.data.data.services.services);
        if (res.data.data.services.services.length > 0 &&  !router.currentRoute.value.path.includes('/settings')) {
          const curService = res.data.data.services.services.find(
            ser =>
              ser.key ===
              context.rootState.databaseState.databaseCommonState
                .currentDatabaseService.key,
          );
        }
        return res;
      });
  },
  getDatabaseGraphData(context: Context, params: Params) {
    const normal = params.dashboardType.isDatabase ? false : true;
    let config = graphHelpers.createConfigDatabase(context, params);
    if (!config || !config.metricName || !config.queryMetricType) {
      return;
    }
    const {
      currentDatabaseService
    } = context.rootState.databaseState.databaseCommonState;
    const { currentDatabaseInstance, currentBucket} = context.rootState.databaseState.detail;
    const CurrentDatabaseServiceLabel = btoa(currentDatabaseService.label)
    const metricNames = params?.metricName ? [params?.metricName] : graphHelpers.parse(config.metricName);
    const labels = graphHelpers.parse(config.labelsIndex || '');
    const variablesList: Array<any> = metricNames.map((name: string) => {
      let variables = {} as any;
      variables = {
        duration: params.duration,
        labels,
      };
      if (
        context.rootState.databaseState.detail.currentSubType.key ===
        constants.SUB_TYPES.CB_CLUSTER
      ) {
        variables.condition = {
          name: name,
          entity: {
            scope: 'ServiceInstance',
            serviceName: currentDatabaseService.key,
            serviceInstanceName:
              currentDatabaseService.key + '_' + CurrentDatabaseServiceLabel,
            normal,
          },
        };
      } else if (
        context.rootState.databaseState.detail.currentSubType.key ===
        constants.SUB_TYPES.CB_BUCKET
      ) {
        variables.condition = {
          name: name,
          entity: {
            scope: 'Endpoint',
            serviceName: currentDatabaseService.key,
            endpointName: currentBucket.key,
            normal,
          },
        };
      } else {
        variables.condition = {
          name: name,
          entity: {
            scope: 'Endpoint',
            serviceName: currentDatabaseService.key,
            endpointName: currentDatabaseInstance.key,
            normal,
          },
        };
      }
      return variables;
    });

    if (!variablesList || !variablesList.length) {
      return;
    }

    return Promise.all(
      variablesList.map((variable: any) => {
        if (variable) {
          return graphQl
            .graphQuery(variable, config.queryMetricType)
            .then((res: AxiosResponse<any>) => {
              const resData = res.data.data;
              return {
                ...resData,
                config,
                metricName: variable.condition.name,
              };
            });
        } else {
          return { config };
        }
      }),
    ).then((data: any) => {
      return data;
    });
  },

  [types.databaseActions.GET_ALL_QUERY_RECORDS](
    context: { commit: Commit },
    params: any,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      if (params) {
        graphQl
          .query(types.queryTypes.QUERY_DATABASE_DETAILS)
          .params(params)
          .then((res: AxiosResponse<any>) => {
            if (!res.data.data) {
              throw Error(res.data);
            } else {
              resolve(res.data.data.readAllSampledRecords);
            }
          })
          .catch((err) => {
            console.log(err);
            reject(err);
          });
      }
    });
  },

  [types.databaseActions.GET_ALL_EVENT_RECORDS](
    context: { commit: Commit },
    params: any,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      if (params) {
        graphQl
          .query(types.queryTypes.QUERY_EVENT_DETAILS)
          .params(params)
          .then((res: AxiosResponse<any>) => {
            if (!res.data.data) {
              throw Error(res.data);
            } else {
              resolve(res.data.data.dbmEventTable);
            }
          })
          .catch((err) => {
            console.log(err);
            reject(err);
          });
      }
    });
  },

  async [types.databaseActions.GET_SESSION_DETAILS](
    context: { commit: Commit },
    params: any,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      if (params) {
        graphQl
          .query(types.queryTypes.QUERY_SESSION_DETAILS)
          .params(params)
          .then((res: AxiosResponse<any>) => {
            if (!res.data.data) {
              throw Error(res.data);
            } else {
              resolve(res.data.data.readLabeledMetricsLatestValue);
            }
          })
          .catch((err) => {
            console.log(err);
            reject(err);
          });
      }
    });
  },
  async [types.databaseActions.GET_AVG_DATA](
    context: { commit: Commit },
    params: {
      duration: any;
      serviceName: String;
      endpointName: String;
      cpuAvgMetricName: String;
      memoryAvgMetricName: String;
    },
  ) {
    let cpuUsage;
    let memoryUsage;
    let avgData = { cpuUsage: null, memoryUsage: null };

    memoryUsage = await graphQl
      .query('queryAvgData')
      .params({
        duration: params.duration,
        condition: {
          name: params.memoryAvgMetricName,
          entity: {
            scope: 'Endpoint',
            serviceName: params.serviceName,
            endpointName: params.endpointName,
            normal: true,
          },
          isLatestValue: true,
          sort: 'DES',
        },
        labels: [''],
      })
      .then((res: AxiosResponse) => {
        if (res.data) {
          return res.data.data.readMetricsValue;
        } else return null;
      })
      .catch((error) => {
        console.log('GET_QUERY failed ' + error.message);
      });
    cpuUsage = await graphQl
      .query('queryAvgData')
      .params({
        duration: params.duration,
        condition: {
          name: params.cpuAvgMetricName,
          entity: {
            scope: 'Endpoint',
            serviceName: params.serviceName,
            endpointName: params.endpointName,
            normal: true,
          },
          isLatestValue: true,
          sort: 'DES',
        },
        labels: [''],
      })
      .then((res: AxiosResponse) => {
        if (res.data) {
          return res.data.data.readMetricsValue;
        } else return null;
      })
      .catch((error) => {
        console.log('GET_QUERY failed ' + error.message);
      });
    avgData.cpuUsage = cpuUsage;
    avgData.memoryUsage = memoryUsage;
    return avgData;
  },

  async [types.databaseActions.GET_DB_DISK_DATA](
    context: Context,
    params: {
      duration: any;
      serviceId: String;
      endpointName: String;
      usedMetricName: string;
      totalMetricName: string;
      availableMetricName: string;
      scope: string;
      serviceInstanceName: string;
    },
  ) {
    if (!params.serviceId) {
      return;
    }
    const currentDatabaseType =
      context.rootState.databaseState.databaseCommonState.currentDatabaseType
        .key;
    let availableBytes: KeyLabel[] = [];
    let usedBytes: KeyLabel[] = [];
    let totalBytes: KeyLabel[] = [];
    usedBytes = await graphQl
      .query(types.queryTypes.QUERY_LABLED_VALUES)
      .params({
        duration: params.duration,
        condition: {
          name: params.usedMetricName,
          entity: {
            scope: params.scope,
            serviceName: params.serviceId,
            endpointName: params.endpointName,
            serviceInstanceName: params.serviceInstanceName,
            normal: true,
          },
          isLatestValue: true,
          sort: 'DES',
        },
        labels: [''],
      })
      .then((res: AxiosResponse) => {
        if (res.data) {
          return res.data.data.readLabeledMetricsLatestValue;
        } else return [];
      })
      .catch((error) => {
        console.log('GET_QUERY failed ' + error.message);
      });
    totalBytes = await graphQl
      .query(types.queryTypes.QUERY_LABLED_VALUES)
      .params({
        duration: params.duration,
        condition: {
          name: params.totalMetricName,
          entity: {
            scope: params.scope,
            serviceName: params.serviceId,
            endpointName: params.endpointName,
            serviceInstanceName: params.serviceInstanceName,
            normal: true,
          },
          isLatestValue: true,
          sort: 'DES',
        },
        labels: [''],
      })
      .then((res: AxiosResponse) => {
        if (res.data) {
          return res.data.data.readLabeledMetricsLatestValue;
        } else return [];
      })
      .catch((error) => {});
    if (currentDatabaseType != types.DatabaseTypes.cb && currentDatabaseType !== types.DatabaseTypes.mg) {
      availableBytes = await graphQl
        .query(types.queryTypes.QUERY_LABLED_VALUES)
        .params({
          duration: params.duration,
          condition: {
            name: params.availableMetricName,
            entity: {
              scope: params.scope,
              serviceName: params.serviceId,
              endpointName: params.endpointName,
              serviceInstanceName: params.serviceInstanceName,
              normal: true,
            },
            isLatestValue: true,
            sort: 'DES',
          },
          labels: [''],
        })
        .then((res: AxiosResponse) => {
          if (res.data) {
            return res.data.data.readLabeledMetricsLatestValue;
          } else return [];
        })
        .catch((error) => {
          console.log('GET_QUERY failed ' + error.message);
        });
    }
    let diskUtility1: any[] = [];
    if (
      usedBytes.length === totalBytes.length &&
      (totalBytes.length === availableBytes.length || currentDatabaseType === types.DatabaseTypes.cb || currentDatabaseType === types.DatabaseTypes.mg)
    ) {
      usedBytes.forEach(function (v: KeyLabel) {
        var diskDataObj: any = {
          used: {
            textValue: i18n.global.t('usedStorage'),
            formatData: true,
            data: {} as KeyLabel,
          },
          total: {
            textValue: i18n.global.t('totalStorage'),
            formatData: true,
            data: {} as KeyLabel,
          },
          available: {
            textValue: i18n.global.t('storageavailable'),
            formatData: true,
            data: {} as KeyLabel,
          },
        };
        diskDataObj.used.data = v;
        diskDataObj.total.data = totalBytes.find(
          (ele) => ele.label === v.label,
        ) as KeyLabel;
        if (currentDatabaseType !== types.DatabaseTypes.cb && currentDatabaseType !== types.DatabaseTypes.mg) {
          diskDataObj.available.data = availableBytes.find(
            (el) => el.label === v.label,
          ) as KeyLabel;
        } else {
          diskDataObj.available.data = {
            label: diskDataObj.total.data.label,
            value: diskDataObj.total.data.value - diskDataObj.used.data.value,
          };
        }
        diskUtility1.push(diskDataObj);
      });
    }
    return diskUtility1;
  },
  [types.databaseActions.GET_QUERY_PERFORMANCE_DATA](
    context: Context,
    params: any,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      graphQl
        .query(types.queryTypes.QUERY_PERFORMANCE)
        .params(params)
        .then((res: AxiosResponse<any>) => {
          if (!res.data.data) {
            throw Error(res.data);
          } else {
            resolve(res.data.data.readAllSampledRecordsByTimeBucket);
          }
        })
        .catch((err) => {
          console.log(err);
          reject(err);
        });
    });
  },
};

export default actions;
